import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmployeeFormDialog from "@/components/dialogs/EmployeeFormDialog";
import EmployeeDetailSheet from "@/components/sheets/EmployeeDetailSheet";
import api from "@/lib/api";
import { Plus, Search } from "lucide-react";

export default function EmployeeRoster() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const filtered = employees.filter((e) =>
    [e.name, e.designation, e.reporting_manager].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <DashboardLayout title="Employee Roster">
      <div className="flex items-center justify-between mb-5">
        <div className="relative w-72">
          <Search className="h-4 w-4 text-[#4B5563] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="employee-search-input"
            placeholder="Search by name, designation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button data-testid="add-employee-button" onClick={() => setAddOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Employee
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="employee-roster-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Designation</th>
              <th className="px-5 py-3 font-medium">Date of Joining</th>
              <th className="px-5 py-3 font-medium">Scope of Work</th>
              <th className="px-5 py-3 font-medium">Reporting Manager</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-[#4B5563]">No employees found.</td></tr>
            )}
            {filtered.map((emp) => (
              <tr
                key={emp.id}
                data-testid={`employee-row-${emp.id}`}
                onClick={() => setSelectedId(emp.id)}
                className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] cursor-pointer transition-colors duration-200"
              >
                <td className="px-5 py-3 font-medium text-[#1C1C1E]">{emp.name}</td>
                <td className="px-5 py-3 text-[#4B5563]">{emp.designation || "—"}</td>
                <td className="px-5 py-3 text-[#4B5563]">{emp.date_of_joining || "—"}</td>
                <td className="px-5 py-3 text-[#4B5563] max-w-[280px] truncate">{emp.sow || "—"}</td>
                <td className="px-5 py-3 text-[#4B5563]">{emp.reporting_manager || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EmployeeFormDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <EmployeeDetailSheet employeeId={selectedId} open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)} onUpdated={load} />
    </DashboardLayout>
  );
}
